<template>
  <div class="py-6">
    <div class="flex items-start justify-between">
      <LabelValue :label="t('Reviews.product.view.title.label').toString()">
        <span data-testid="product-title"> {{ product.title }}</span>
      </LabelValue>
      <OpenProductProfile class="shrink-0" :slug="product.slug" />
    </div>
    <div class="grid grid-cols-5">
      <LabelValue class="col-span-1 laptop:col-span-2" :label="t('Reviews.product.view.logo.label').toString()">
        <ProductLogo v-if="product.logo" :logo-url="product.logo.url" size="large" data-testid="product-logo" />
        <span v-else>{{ t('Reviews.product.view.logo.missing') }}</span>
      </LabelValue>
      <LabelValue class="col-span-1 laptop:col-span-3" :label="t('Reviews.product.view.hero.label').toString()">
        <div v-if="product.hero" class="max-w-md">
          <AssetPreview
            data-testid="product-hero-image"
            :content-type="product.hero.contentType"
            :disabled="!product.configFlags.heroActive"
            :name="product.hero.name"
            :poster-url="product.heroVideoThumbnail?.url"
            :url="product.hero.url"
          />
        </div>
        <template v-else>
          <span v-if="product.configFlags.heroActive">{{ t('Reviews.product.view.hero.missing') }}</span>
          <span v-else>{{ t('Reviews.product.view.hero.notActive') }}</span>
        </template>
      </LabelValue>
    </div>
    <LabelValue :label="t('Reviews.product.view.category.label').toString()" data-testid="product-category">
      <div class="flex flex-wrap items-center gap-2">
        <ChipBadge
          v-for="(category, index) in productCategories"
          :key="index"
          rounded
          :type="index === 0 ? 'solid' : 'default'"
          color="purple"
          >{{ category }}</ChipBadge
        >
      </div>
    </LabelValue>
    <LabelValue :label="t('Reviews.product.view.description.label').toString()" data-testid="product-short-desctiption">
      <div class="grid grid-cols-2 gap-x-6">
        <div
          v-for="lang in ALLOWED_LOCALES"
          :key="lang"
          class="flex flex-col items-start gap-y-2"
          :data-testid="'short-description-' + lang"
        >
          <div class="flex w-full items-center gap-x-2">
            <span class="text-body-s-bold text-main">
              {{ t('Reviews.product.view.description.shortIntro.label') }}
            </span>
            <ChipBadge class="uppercase" rounded>{{ lang }}</ChipBadge>
          </div>
          <span v-if="product.shortDescriptionTranslationsMap[lang]" :data-testid="'short-description-text-' + lang">{{
            product.shortDescriptionTranslationsMap[lang]
          }}</span>
          <span v-else class="text-main">{{ t('Reviews.product.view.shortDescription.missing') }}</span>
        </div>
      </div>
      <Expandable class="mt-24" :max-height="247">
        <div class="grid grid-cols-2 gap-x-6" data-testid="product-description">
          <div v-for="lang in ALLOWED_LOCALES" :key="lang" class="flex flex-col items-start gap-y-2" :data-testid="'description-' + lang">
            <div class="flex w-full items-center gap-x-2">
              <span class="text-body-s-bold text-main">
                {{ t('Reviews.product.view.description.full.label') }}
              </span>
              <ChipBadge class="uppercase" rounded>{{ lang }}</ChipBadge>
            </div>
            <div>
              <MarkdownContent
                v-if="product.descriptionTranslationsMap[lang]"
                :md="product.descriptionTranslationsMap[lang]"
                :data-testid="'description-text-' + lang"
              />
              <span v-else class="text-main">{{ t('Reviews.product.view.description.missing') }}</span>
            </div>
          </div>
        </div>
      </Expandable>
    </LabelValue>
    <LabelValue :label="t('Reviews.product.view.links.label').toString()" data-testid="product-links">
      <div class="grid grid-cols-2 gap-x-6 gap-y-2">
        <div v-for="lang in ALLOWED_LOCALES" :key="lang">
          <ChipBadge class="mb-4 uppercase" rounded>{{ lang }}</ChipBadge>
          <div class="flex items-center">
            <MdiSvg :path="mdiWeb" :size="24" class="social-link-icon" />
            <a
              v-if="product.urlTranslationsMap[lang]"
              class="overflow-hidden text-ellipsis whitespace-nowrap"
              :href="product.urlTranslationsMap[lang]"
              :data-testid="`product-website-${lang}`"
            >
              {{ product.linkTextTranslationsMap[lang] || product.urlTranslationsMap[lang] }}
            </a>
            <span v-else class="text-main">{{ t('Reviews.product.view.url.missing') }}</span>
          </div>
        </div>
        <div class="flex items-center">
          <MdiSvg :path="mdiLinkedin" :size="24" class="social-link-icon" />
          <a
            v-if="product.linkedin"
            class="overflow-hidden text-ellipsis whitespace-nowrap"
            :href="product.linkedin"
            data-testid="product-linkedin"
          >
            {{ product.linkedin }}
          </a>
          <span v-else class="text-main">{{ t('Reviews.product.view.linkedin.missing') }}</span>
        </div>
        <div />
        <div class="flex items-center">
          <MdiSvg :path="iconTwitterX" :size="24" class="social-link-icon" />
          <a
            v-if="product.twitter"
            class="overflow-hidden text-ellipsis whitespace-nowrap"
            :href="product.twitter"
            data-testid="product-twitter"
          >
            {{ product.twitter }}
          </a>
          <span v-else class="text-main">{{ t('Reviews.product.view.twitter.missing') }}</span>
        </div>
      </div>
    </LabelValue>
    <ReviewsCtaView :data="product" />
  </div>
</template>
<script setup lang="ts">
import { mdiLinkedin, mdiWeb } from '@mdi/js'
import { ChipBadge, Expandable, MarkdownContent, MdiSvg, iconTwitterX } from '@ramp106/omrjs-core-ui'
import { ProductLogo } from '@ramp106/omrjs-reviews-ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import OpenProductProfile from '@/components/ReviewsManagement/OpenProductProfile.vue'
import ReviewsCtaView from '@/components/ReviewsManagement/ReviewsCtaView.vue'
import type { ProductData } from '@/gql/reviews'
import { getProductCategoryLabels } from '@/helpers/productCategoriesHelper'
import { ALLOWED_LOCALES } from '@/helpers/translationsHelper'
import AssetPreview from '@/ui/AssetPreview.vue'
import LabelValue from '@/ui/LabelValue.vue'

const props = defineProps<{
  product: ProductData
}>()

const { t } = useI18n()

const productCategories = computed(() => getProductCategoryLabels(props.product, t('Reviews.product.view.category.main')))
</script>

<style scoped>
@reference '@ramp106/omrjs-config-tailwind/styles/index.css';

.social-link-icon {
  @apply mr-1 flex-shrink-0 text-main;
}
</style>
